// ----------------------------------------------------------------------------
// Enhanced line-item quantity picker.
//
// The cart line item uses a custom quantity picker (.line-item__quantity-picker)
// with explicit minus/plus buttons (data-action="decrease|increase"). The stock
// `line-item-quantity` element in theme.js only reacts to the input's `change`
// event - it has no handling for those buttons.
//
// This file registers `line-item-quantity` with a class that DOES handle the
// buttons (and the remove link), and it is loaded as a module BEFORE theme.js so
// it wins the customElements.define() race. theme.js guards its own definition
// with `if (!customElements.get('line-item-quantity'))`, so it will skip and this
// enhanced version stays in place on every page and after every AJAX cart render.
//
// Previously this lived in an inline <script> inside snippets/line-item.liquid.
// That only executed when a line item happened to be present in the initial
// server-rendered HTML; when the drawer was rebuilt via AJAX (empty cart then
// add, returning from checkout, etc.) the inline script never ran, the stock
// version had already claimed the element name, and the +/- buttons went dead.
// ----------------------------------------------------------------------------

class LineItemQuantityEnhanced extends HTMLElement {
  constructor() {
    super();
    this._onButtonClick = this._onButtonClick.bind(this);
    this._onQuantityChanged = this._onQuantityChanged.bind(this);
    this._onRemoveLinkClicked = this._onRemoveLinkClicked.bind(this);
  }

  connectedCallback() {
    this.inputElement = this.querySelector('input[data-line-key]');
    this.minusButton = this.querySelector('[data-action="decrease"]');
    this.plusButton = this.querySelector('[data-action="increase"]');

    if (this.minusButton) {
      this.minusButton.addEventListener('click', this._onButtonClick);
    }
    if (this.plusButton) {
      this.plusButton.addEventListener('click', this._onButtonClick);
    }
    if (this.inputElement) {
      this.inputElement.addEventListener('change', this._onQuantityChanged);
    }

    // Handle remove link clicks (bubbling from the <a>).
    this.addEventListener('click', this._onRemoveLinkClicked);

    this._updateButtonStates();
  }

  disconnectedCallback() {
    if (this.minusButton) {
      this.minusButton.removeEventListener('click', this._onButtonClick);
    }
    if (this.plusButton) {
      this.plusButton.removeEventListener('click', this._onButtonClick);
    }
    if (this.inputElement) {
      this.inputElement.removeEventListener('change', this._onQuantityChanged);
    }
  }

  _onButtonClick(event) {
    event.preventDefault();
    if (!this.inputElement) return;

    const action = event.currentTarget.dataset.action;
    const currentValue = parseInt(this.inputElement.value) || 1;
    const min = parseInt(this.inputElement.min) || 1;
    const max = this.inputElement.max ? parseInt(this.inputElement.max) : Infinity;
    const step = parseInt(this.inputElement.step) || 1;

    let newValue = currentValue;

    if (action === 'decrease') {
      newValue = Math.max(min, currentValue - step);
      // If already at the minimum, decreasing removes the item from the cart.
      if (currentValue <= min) {
        newValue = 0;
      }
    } else if (action === 'increase') {
      newValue = Math.min(max, currentValue + step);
    }

    if (newValue !== currentValue) {
      this.inputElement.value = newValue;
      this._changeLineItemQuantity(this.inputElement.dataset.lineKey, newValue);
    }
  }

  _onQuantityChanged(event) {
    if (!event.target.hasAttribute('data-line-key')) {
      return;
    }
    this._changeLineItemQuantity(event.target.dataset.lineKey, parseInt(event.target.value));
  }

  _onRemoveLinkClicked(event) {
    if (event.target.tagName !== 'A' || !event.target.href.includes('/cart/change')) {
      return;
    }
    event.preventDefault();
    const url = new URL(event.target.href);
    const lineKey = url.searchParams.get('id') || url.searchParams.get('line');
    this._changeLineItemQuantity(lineKey, parseInt(url.searchParams.get('quantity')));
  }

  _updateButtonStates() {
    if (!this.inputElement) return;

    const currentValue = parseInt(this.inputElement.value) || 1;
    const max = this.inputElement.max ? parseInt(this.inputElement.max) : Infinity;

    // Minus is never disabled (clicking at the minimum removes the item).
    // Plus is disabled once the maximum is reached.
    if (this.plusButton) {
      this.plusButton.disabled = currentValue >= max;
    }
  }

  async _changeLineItemQuantity(lineKey, targetQuantity) {
    const lineItem = this.closest('line-item');
    lineItem?.dispatchEvent(new CustomEvent('line-item:will-change', { bubbles: true, detail: { targetQuantity } }));

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: targetQuantity,
        sections: sectionsToBundle
      })
    });

    if (!response.ok) {
      const responseContent = await response.json();
      this.closest('.line-item, tr').querySelector('[role="alert"]')?.remove();
      const errorSvg = `<svg width="14" viewBox="0 0 18 18">
        <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path>
        <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
      </svg>`;
      this.closest('.line-item, tr').querySelector('.line-item__info').insertAdjacentHTML('beforeend', `<p class="text-with-icon text-error text-xs" role="alert">${errorSvg} ${responseContent['description']}</p>`);
      this.inputElement.value = this.inputElement.defaultValue;
      lineItem?.dispatchEvent(new CustomEvent('line-item:error', { bubbles: true }));
    } else {
      const cartContent = await response.json();

      if (window.themeVariables.settings.pageType === 'cart') {
        window.location.reload();
      } else {
        const lineItemAfterChange = cartContent['items'].filter((item) => item['key'] === lineKey);
        lineItem?.dispatchEvent(new CustomEvent('line-item:change', {
          bubbles: true,
          detail: {
            quantity: lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0]['quantity'],
            cart: cartContent
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'line-item:change',
            cart: cartContent
          }
        }));
      }
    }

    this._updateButtonStates();
  }
}

// Register before theme.js so the enhanced (button-aware) version wins.
if (!window.customElements.get('line-item-quantity')) {
  window.customElements.define('line-item-quantity', LineItemQuantityEnhanced);
}